
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { RequiredValidator } from "@dev/hc-components";
import Factor from "@/app/dto/FactorEnum";
import IContractWithdrawalIdentifyResponse from "@/app/dto/IContractWithdrawalIdentifyResponse";
import IAllowedFactorsResponse from "@/app/dto/IAllowedFactorsResponse";
import IAllowedFactor from "@/app/dto/IAllowedFactor";
import IPhoneNumber from "@/app/dto/IPhoneNumber";
import ProcessEnum from "@/app/dto/ProcessEnum";
import ProcessResultKey from "@/app/dto/ProcessResultKeyEnum";
import IApiError from "@/app/model/IApiError";
import RouteName from "@/app/router/NameEnum";
import AuthorizationService from "@/app/service/authorization";
import ContractWithdrawalService from "@/app/service/contract";

@Component
export default class ContractWithdrawalIdentify extends Vue {

    private name: string = "";
    private lastName: string = "";
    private contractNumber: string = "";
    private nameValidator: RequiredValidator = new RequiredValidator(this.$localize("contract_withdrawal.name.required"));
    private lastNameValidator: RequiredValidator = new RequiredValidator(this.$localize("contract_withdrawal.last_name.required"));
    private contractNumberValidator: RequiredValidator = new RequiredValidator(this.$localize("contract_withdrawal.contract_number.required"));
    private submitting: boolean = false;

    private onSubmit(): void {
        if (!this.isValid) {
            return;
        }
        this.submitting = true;
        ContractWithdrawalService.identify(this.name, this.lastName, this.contractNumber)
            .then((response: IContractWithdrawalIdentifyResponse) => {
                return this.getFactors(response.token.accessToken, response.phoneNumber, response.email);
            })
            .catch((error: IApiError) => {
                this.processIdentifyError(error);
            })
            .finally(() => {
                this.submitting = false;
            });
    }

    private get isValid(): boolean {
        const validName = this.nameValidator.validate(this.name);
        const validLastName = this.lastNameValidator.validate(this.lastName);
        const validContractNumber = this.contractNumberValidator.validate(this.contractNumber);
        return validName && validLastName && validContractNumber;
    }

    private processIdentifyError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";

        switch (error.status) {
            case ProcessResultKey.CONTRACT_NOT_FOUND:
                errorHeading = this.$localize("error.contract.not_found.title");
                errorMessage = this.$localize("error.contract.not_found.message");
                break;
            case ProcessResultKey.INVALID_CONTRACT:
                errorHeading = this.$localize("error.contract.invalid.title");
                errorMessage = this.$localize("error.contract.invalid.message");
                break;
            case ProcessResultKey.INVALID_CONTRACT_DATA:
                errorHeading = this.$localize("error.contract.invalid_data.title");
                errorMessage = this.$localize("error.contract.invalid_data.message");
                break;
        }
        this.routeToError({ heading: errorHeading, message: errorMessage });
    }

    private routeToError(params?: { heading: string; message: string }): void {
        this.$router.push({ name: RouteName.CONTRACT_WITHDRAWAL_ERROR, params });
    }

    private getFactors(token: string, phoneNumber: IPhoneNumber, email: string): Promise<void> {
        return AuthorizationService.getAllowedFactorsProtected(token, ProcessEnum.CONTRACT_WITHDRAWAL)
            .then((response: IAllowedFactorsResponse) => {
                if (this.hasOtp(response)) {
                    this.$router.push({
                        name: RouteName.CONTRACT_WITHDRAWAL_OTP,
                        params: {
                            token: token,
                            contractNumber: this.contractNumber,
                            email: email,
                            countryCode: phoneNumber.countryCode,
                            number: phoneNumber.number
                        }
                    });
                } else {
                    this.routeToError();
                }
            })
            .catch((error: IApiError) => {
                this.routeToError();
            });
    }

    private hasOtp(response: IAllowedFactorsResponse): boolean {
        return !!response.data!!.find((f: IAllowedFactor) => f.factors!! && f.factors?.includes(Factor.OTP));
    }
}
